import { PluginData } from './types';  
import { handleApiResponse, ApiError } from '../common';
import { getApiBaseUrl, API_VERSION } from '../index';

const DISPLAY_MODE = {
  WINDOWED: 'WINDOWED',
  FULLSCREEN: 'FULLSCREEN',
  DEFAULT: 'WINDOWED'
};

const normalizeDisplayMode = (mode) => {
  if (!mode) return DISPLAY_MODE.DEFAULT;
  const normalized = mode.toUpperCase();
  return normalized === DISPLAY_MODE.FULLSCREEN ? DISPLAY_MODE.FULLSCREEN : DISPLAY_MODE.WINDOWED;
};

export const pluginApi = {
  async list() {
    try {

      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/plugins/list`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();

      if (!Array.isArray(data.plugins)) {
        console.error('Unexpected data format:', data);
        throw new Error('Invalid data format received from API');
      }

      const transformedData = PluginData.fromApiResponseList(data.plugins);
      return transformedData;
    } catch (error) {
      console.error('Failed to fetch plugins:', error);
      throw error;
    }
  },

  async sync() {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/plugins/sync`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();
      return data;
    } catch (error) {
      console.error('Failed to sync plugins:', error);
      throw error;
    }
  },

  async create(plugins) {
    try {
      const formattedPlugins = plugins.map(p => {
        const basePlugin = p instanceof PluginData ? p.toApiFormat() : p;
        const displayMode = normalizeDisplayMode(basePlugin.display_mode);
        
        return {
          name: basePlugin.name || '',
          category: basePlugin.category || '',
          url: basePlugin.url || '',
          main_branch: basePlugin.main_branch || 'main',
          beta_branch: basePlugin.beta_branch || '',
          enabled: basePlugin.enabled ?? true,
          display_order: parseInt(basePlugin.display_order) || 0,
          description: basePlugin.description || '',
          author: basePlugin.author || '',
          wiki_url: basePlugin.wiki_url || null,
          plugin_type: basePlugin.plugin_type || 'WEB',
          display_mode: displayMode,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          thumbnail_url: null,
          thumbnail_updated_at: null
        };
      });

      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/plugins/create`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ plugins: formattedPlugins })
      });

      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();
      return PluginData.fromApiResponseList(data.plugins);
    } catch (error) {
      console.error('Failed to create plugins:', error);
      throw error;
    }
  },

  async update(plugins) {
    try {
      const formattedPlugins = plugins.map(p => {
        const basePlugin = p instanceof PluginData ? p.toApiFormat() : p;
        const displayMode = normalizeDisplayMode(basePlugin.display_mode);

        return {
          plugin_id: basePlugin.plugin_id,
          name: basePlugin.name,
          category: basePlugin.category,
          url: basePlugin.url,
          main_branch: basePlugin.main_branch,
          beta_branch: basePlugin.beta_branch,
          enabled: basePlugin.enabled ?? true,
          display_order: parseInt(basePlugin.display_order) || 0,
          description: basePlugin.description || '',
          author: basePlugin.author || '',
          wiki_url: basePlugin.wiki_url || null,
          plugin_type: basePlugin.plugin_type || 'WEB',
          display_mode: displayMode,
          created_at: basePlugin.created_at || new Date().toISOString(),
          updated_at: new Date().toISOString(),
          thumbnail_url: basePlugin.thumbnail_url || null,
          thumbnail_updated_at: basePlugin.thumbnail_updated_at || null
        };
      });

      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/plugins/update`, {
        method: 'PUT',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ plugins: formattedPlugins })
      });

      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();
      return PluginData.fromApiResponseList(data.plugins);
    } catch (error) {
      console.error('Failed to update plugins:', error);
      throw error;
    }
  },

  async delete(pluginIds) {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/plugins/remove`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ plugin_ids: pluginIds })
      });

      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();
      return data.deleted_ids;
    } catch (error) {
      console.error('Failed to delete plugins:', error);
      throw error;
    }
  },

  async uploadThumbnail(pluginId, file) {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/plugins/thumbnail/${pluginId}`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      const validatedResponse = await handleApiResponse(response);
      const data = await validatedResponse.json();
      return {
        plugin_id: data.plugin_id,
        thumbnail_url: data.thumbnail_url,
        thumbnail_updated_at: data.thumbnail_updated_at
      };
    } catch (error) {
      console.error('Failed to upload thumbnail:', error);
      throw error;
    }
  },

  async getThumbnail(pluginId) {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/plugins/thumbnail/${pluginId}`, {
        credentials: 'include',
      });
      
      if (response.status === 404 || response.status === 500) {
        console.warn(`No thumbnail available for plugin ${pluginId}`);
        return null;
      }
      
      const validatedResponse = await handleApiResponse(response);
      return validatedResponse.blob();
    } catch (error) {
      console.error('Failed to fetch thumbnail:', error);
      return null;
    }
  },

  async collectCodebase() {
    try {
      const response = await fetch(`${getApiBaseUrl()}${API_VERSION}/plugins/collect_codebase`, {
        method: 'GET',
        credentials: 'include',
      });
      
      const validatedResponse = await handleApiResponse(response);
      
      if (validatedResponse.status === 200) {
        return validatedResponse.blob();
      } else if (validatedResponse.status === 404) {
        throw new ApiError('No WEB-type plugins found', 404);
      }
    } catch (error) {
      console.error('Failed to collect codebase:', error);
      throw error;
    }
  },

  async downloadCodebase() {
    try {
      const blob = await this.collectCodebase();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'codebase.tar.gz';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to download codebase:', error);
      throw error;
    }
  }
};